// DataCards — generic card grid: equal-height card shells in a width-driven
// grid (each row fits as many minCardWidth-wide cards as the space allows, so
// wider screens show more per row), with loading/error/empty states. This owns
// the outer layer only — shell, grid, states; what renders
// inside a card is module-specific via renderCard (body) and renderFooter
// (bottom-pinned strip behind a full-width divider). The caller owns data
// fetching; pagination is load-more style via the CardsLoadMore sibling
// (loading here = first page: isLoading || (isFetching && page === 1)).

import type { CSSProperties, ReactNode } from 'react';

export interface DataCardsProps<T> {
  items: T[];
  itemKey: (item: T) => string;
  /** Fills the card body (the growing, padded area above the footer). */
  renderCard: (item: T) => ReactNode;
  /** Optional footer strip — pinned to the card bottom behind a divider. */
  renderFooter?: (item: T) => ReactNode;
  /** Minimum card width (px) — the per-row count is however many fit. Default 260. */
  minCardWidth?: number;
  onItemClick?: (item: T) => void;
  /** Extra class(es) on an item shell (e.g. a page-level selected modifier). */
  itemClassName?: (item: T) => string;
  loading?: boolean;
  error?: boolean;
  /** Custom empty-state node (icon + text). */
  empty?: ReactNode;
  /** Fallback texts for loading/error states (already translated). */
  loadingText?: string;
  errorText?: string;
}

const DataCards = <T,>({
  items,
  itemKey,
  renderCard,
  renderFooter,
  minCardWidth = 260,
  onItemClick,
  itemClassName,
  loading = false,
  error = false,
  empty,
  loadingText,
  errorText,
}: DataCardsProps<T>) => {
  const rootStyle = { '--dc-min': `${minCardWidth}px` } as CSSProperties;

  return (
    <div className='data-cards' style={rootStyle}>
      <div className='data-cards__grid'>
        {!loading &&
          !error &&
          items.map((item) => {
            const extra = itemClassName?.(item);
            return (
              <div
                key={itemKey(item)}
                className={`data-cards__item${
                  onItemClick ? ' data-cards__item--clickable' : ''
                }${extra ? ` ${extra}` : ''}`}
                {...(onItemClick
                  ? {
                      role: 'button',
                      tabIndex: 0,
                      onClick: () => onItemClick(item),
                      onKeyDown: (e: React.KeyboardEvent) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          onItemClick(item);
                        }
                      },
                    }
                  : {})}
              >
                <div className='data-cards__body'>{renderCard(item)}</div>
                {renderFooter && <div className='data-cards__footer'>{renderFooter(item)}</div>}
              </div>
            );
          })}
      </div>

      {loading ? (
        <div className='data-cards__status'>{loadingText}</div>
      ) : error ? (
        <div className='data-cards__status'>{errorText}</div>
      ) : items.length === 0 ? (
        <div className='data-cards__status'>{empty}</div>
      ) : null}
    </div>
  );
};

export default DataCards;
